import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge, Box, Card, Code, Heading, Table, Text } from "@radix-ui/themes";
import {
  PushSendLog,
  PushSendStatus,
  tenantPushService,
} from "@/services/breeze/TenantPushService";
import { formatDate } from "@/utils/date";

const STATUS_COLOR: Record<PushSendStatus, "gray" | "blue" | "green" | "amber" | "red"> = {
  Pending: "gray",
  Running: "blue",
  Succeeded: "green",
  Partial: "amber",
  Failed: "red",
  Unknown: "gray",
};

export default function PushSendHistory() {
  const [expanded, setExpanded] = useState<number | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["push-send-logs"],
    queryFn: () => tenantPushService.listSendLogs(1, 20),
    refetchOnMount: "always",
    staleTime: 0,
    refetchInterval: (query) => {
      const items = (query.state.data as { items?: PushSendLog[] } | undefined)?.items ?? [];
      return items.some((r) => r.status === "Pending" || r.status === "Running") ? 3000 : false;
    },
  });

  const items = data?.items ?? [];

  return (
    <Box>
      <Heading size="3" mb="2">Recent sends</Heading>
      <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
        Last 20 sends. In-flight rows refresh every 3s.
      </Text>
      {isLoading && items.length === 0 ? (
        <Text size="2" color="gray">Loading…</Text>
      ) : items.length === 0 ? (
        <Text size="2" color="gray">No sends yet.</Text>
      ) : (
        <Card style={{ contain: "none" }}>
          <Table.Root size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>When</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Category</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Audience</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Results</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((row) => {
                const isOpen = expanded === row.id;
                return (
                  <>
                    <Table.Row
                      key={row.id}
                      onClick={() => setExpanded(isOpen ? null : row.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <Table.Cell>
                        <Text size="1">{formatDate(row.queued_at)}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color={STATUS_COLOR[row.status]} variant="soft">
                          {row.status}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <SourcePill row={row} />
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="1">{row.title ?? "—"}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        {row.category
                          ? <Code size="1" variant="ghost">{row.category}</Code>
                          : <Text size="1" color="red">none</Text>}
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="1" color="gray">{row.audience_summary ?? row.audience_kind}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <ProviderTally json={row.per_provider_json} />
                      </Table.Cell>
                    </Table.Row>
                    {isOpen && (
                      <Table.Row key={`${row.id}-expanded`}>
                        <Table.Cell colSpan={7}>
                          <Box p="2" style={{ background: "var(--gray-a2)", borderRadius: "var(--radius-2)" }}>
                            {row.error_message && (
                              <Text size="1" color="red" mb="1" style={{ display: "block" }}>
                                Error: {row.error_message}
                              </Text>
                            )}
                            <Text size="1" color="gray" style={{ display: "block", fontFamily: "monospace" }}>
                              {row.per_provider_json ?? "(no per-provider data yet)"}
                            </Text>
                          </Box>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </>
                );
              })}
            </Table.Body>
          </Table.Root>
        </Card>
      )}
    </Box>
  );
}

function SourcePill({ row }: { row: PushSendLog }) {
  const src = row.source ?? "send_test";
  if (src === "workflow") {
    return (
      <Text size="1">
        <Badge color="violet" variant="soft">Workflow</Badge>
        {row.workflow_name && (
          <Text size="1" color="gray" ml="1">{row.workflow_name}</Text>
        )}
      </Text>
    );
  }
  if (src === "broadcast") {
    return <Badge color="orange" variant="soft">Broadcast</Badge>;
  }
  if (src === "send_test") {
    return <Badge color="gray" variant="soft">Test</Badge>;
  }
  return <Badge color="gray" variant="soft">{src}</Badge>;
}

function ProviderTally({ json }: { json: string | null }) {
  if (!json) return <Text size="1" color="gray">—</Text>;
  try {
    const parsed = JSON.parse(json) as Record<string, { Success: number; Failure: number; Unregistered: number }>;
    const parts = Object.entries(parsed).map(([k, v]) => `${k}: ${v.Success}/${v.Success + v.Failure}`);
    return <Text size="1">{parts.join(" · ")}</Text>;
  } catch {
    return <Text size="1" color="gray">?</Text>;
  }
}
